import { useState, useEffect, useCallback, useMemo } from 'react';
import { Radio, X, ChevronRight, Check, RotateCcw, Minus } from 'lucide-react';
import { useFlashDeck, type FlashCard } from '@/hooks/useFlashDeck';
import {
  getConsent, setConsent, isDismissedSession, dismissSession,
  getLearned, toggleLearned, resetLearned,
} from '@/lib/flashMemoryStore';

import { useIsMobile } from '@/hooks/use-mobile';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { TickerSettings, type TickerSettingsData } from '@/components/TickerSettings';

const QUESTION_MS = 5000;
const ANSWER_MS = 4000;

type Phase = 'consent' | 'active' | 'expanded' | 'minimized' | 'hidden';

const CATEGORY_COLORS: Record<string, string> = {
  general: 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30',
  radiotelephony: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
  asn: 'bg-red-500/20 text-red-400 border-red-500/30',
  phonetic: 'bg-violet-500/20 text-violet-400 border-violet-500/30',
};

const CATEGORY_LABELS: Record<string, string> = {
  general: 'Général',
  radiotelephony: 'Radio',
  asn: 'ASN',
  phonetic: 'Phonétique',
};

function pickNext(
  current: number,
  cards: FlashCard[],
  learned: Record<string, boolean>,
  hideLearned: boolean,
): number {
  const pool: number[] = [];
  const learnedPool: number[] = [];

  cards.forEach((q, i) => {
    if (i === current) return;
    if (learned[q.id]) {
      learnedPool.push(i);
    } else {
      pool.push(i);
    }
  });

  if (!hideLearned) {
    learnedPool.forEach((i) => {
      if (Math.random() < 0.2) pool.push(i);
    });
  }

  if (pool.length === 0) {
    return (current + 1) % cards.length;
  }
  return pool[Math.floor(Math.random() * pool.length)];
}

export const CRRFlashTicker = () => {
  const isMobile = useIsMobile();
  const isFr = true; // CRR is a French maritime exam — always display in French
  const { data: cards = [], isLoading } = useFlashDeck();

  const [phase, setPhase] = useState<Phase>(() => {
    if (isDismissedSession()) return 'hidden';
    if (getConsent()) return isMobile ? 'minimized' : 'active';
    return 'consent';
  });

  const [index, setIndex] = useState(0);
  const [showAnswer, setShowAnswer] = useState(false);
  const [transitioning, setTransitioning] = useState(false);
  const [learned, setLearned] = useState<Record<string, boolean>>(getLearned);
  const [settings, setSettings] = useState<TickerSettingsData>({ hideLearned: false, reducedMotion: false });

  const allLearned = useMemo(
    () => cards.length > 0 && cards.every((q) => learned[q.id]),
    [learned, cards],
  );

  const advance = useCallback(() => {
    if (cards.length === 0) return;
    setTransitioning(true);
    setTimeout(() => {
      setIndex((prev) => pickNext(prev, cards, learned, settings.hideLearned));
      setShowAnswer(false);
      setTransitioning(false);
    }, settings.reducedMotion ? 0 : 300);
  }, [learned, cards, settings.hideLearned, settings.reducedMotion]);

  useEffect(() => {
    if (phase !== 'active' || cards.length === 0) return;
    const timer = setTimeout(
      () => {
        if (!showAnswer) {
          setShowAnswer(true);
        } else {
          advance();
        }
      },
      showAnswer ? ANSWER_MS : QUESTION_MS,
    );
    return () => clearTimeout(timer);
  }, [showAnswer, phase, index, advance, cards.length]);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && (phase === 'expanded' || phase === 'active')) {
        setPhase('minimized');
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [phase]);

  const handleConsent = (accepted: boolean) => {
    if (accepted) {
      setConsent(true);
      setPhase(isMobile ? 'minimized' : 'active');
    } else {
      dismissSession();
      setPhase('hidden');
    }
  };

  const handleToggleLearned = () => {
    if (!current) return;
    const updated = toggleLearned(current.id);
    setLearned({ ...updated });
  };

  const handleResetLearned = () => {
    resetLearned();
    setLearned({});
  };

  if (phase === 'hidden' || isLoading || cards.length === 0) return null;

  const current = cards[index % cards.length];
  if (!current) return null;

  const q = isFr ? current.question_fr : current.question_en;
  const a = isFr ? current.answer_fr : current.answer_en;
  const ctx = isFr ? (current.explanation_fr || current.context_fr) : (current.explanation_en || current.context_en);
  const useCases = isFr ? (current.use_cases_fr || []) : (current.use_cases_en || []);
  const isLearned = !!learned[current.id];
  const learnedCount = Object.keys(learned).length;

  // ── Consent Card ──
  if (phase === 'consent') {
    return (
      <div className="fixed bottom-4 left-4 z-40 max-w-xs" role="complementary" aria-label="CRR Flash Questions">
        <div className="rounded-lg border border-primary/30 bg-background/95 backdrop-blur-sm shadow-lg shadow-primary/10 p-3">
          <div className="flex items-center gap-1.5 mb-2">
            <Radio className="w-4 h-4 text-primary" />
            <span className="text-xs font-orbitron font-bold text-primary tracking-widest uppercase">Flash CRR</span>
          </div>
          <p className="text-xs text-muted-foreground leading-relaxed tracking-wide mb-1.5">
            {isFr ? 'Apprenez les termes radio maritimes en naviguant sur le site.' : 'Learn maritime radio terms while browsing the site.'}
          </p>
          <p className="text-[9px] text-muted-foreground/50 leading-relaxed tracking-wide mb-3">
            {isFr ? 'Contenu éducatif non officiel — consultez l\'ANFR pour les sources officielles.' : 'Unofficial educational content — consult ANFR for official sources.'}
          </p>
          <div className="flex gap-2">
            <Button size="sm" className="text-xs h-7 px-3 bg-primary text-primary-foreground hover:bg-primary/90" onClick={() => handleConsent(true)} aria-label={isFr ? 'Activer les flash questions' : 'Activate flash questions'}>
              {isFr ? 'Activer' : 'Activate'}
            </Button>
            <Button size="sm" variant="ghost" className="text-xs h-7 px-3 text-muted-foreground" onClick={() => handleConsent(false)} aria-label={isFr ? 'Pas maintenant' : 'Not now'}>
              {isFr ? 'Pas maintenant' : 'Not now'}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // ── Minimized Pill ──
  if (phase === 'minimized') {
    return (
      <div className="fixed bottom-4 left-4 z-40" role="complementary" aria-label="CRR Flash Questions">
        <button
          onClick={() => setPhase('active')}
          className="flex items-center gap-1.5 rounded-full border border-primary/30 bg-background/95 backdrop-blur-sm shadow-lg shadow-primary/10 px-3 py-1.5 hover:border-primary/60 transition-colors"
          aria-label={isFr ? 'Ouvrir les flash questions CRR' : 'Open CRR flash questions'}
        >
          <Radio className={`w-3.5 h-3.5 text-primary ${settings.reducedMotion ? '' : 'motion-safe:animate-pulse'}`} />
          <span className="text-[10px] font-orbitron font-bold text-primary tracking-widest">⚓ CRR</span>
        </button>
      </div>
    );
  }

  // ── Expanded View ──
  if (phase === 'expanded') {
    return (
      <div className="fixed bottom-4 left-4 z-40 max-w-sm" role="complementary" aria-label="CRR Flash Questions">
        <div className="rounded-lg border border-primary/30 bg-background/95 backdrop-blur-sm shadow-lg shadow-primary/10">
          <div className="flex items-center justify-between px-3 pt-2.5 pb-1">
            <div className="flex items-center gap-1.5">
              <Radio className="w-3.5 h-3.5 text-primary" />
              <span className="text-[10px] font-orbitron font-bold text-primary tracking-widest uppercase">
                {current.acronym || 'Flash Question'}
              </span>
            </div>
            <div className="flex items-center gap-1">
              <Badge variant="outline" className={`text-[9px] px-1.5 py-0 h-4 border ${CATEGORY_COLORS[current.category] || ''}`}>
                {CATEGORY_LABELS[current.category] || current.category}
              </Badge>
              <TickerSettings onSettingsChange={setSettings} />
              <button onClick={() => setPhase('minimized')} className="p-1 rounded-full hover:bg-accent/20 text-muted-foreground hover:text-foreground transition-colors" aria-label={isFr ? 'Réduire' : 'Minimize'}>
                <Minus className="w-3 h-3" />
              </button>
            </div>
          </div>

          <div className="px-3 pb-3" aria-live="polite">
            <p className="text-sm text-foreground leading-relaxed tracking-wide font-medium">{q}</p>
            <p className="text-sm text-primary font-semibold mt-1.5 leading-relaxed tracking-wide">→ {a}</p>
            <p className="text-xs text-muted-foreground mt-2 leading-relaxed tracking-wide italic">{ctx}</p>

            {/* Use cases */}
            {useCases.length > 0 && (
              <ul className="mt-2 space-y-1">
                {useCases.map((uc, i) => (
                  <li key={i} className="flex items-start gap-1.5 text-[10px] text-muted-foreground/80 tracking-wide">
                    <span className="text-primary mt-0.5">•</span>
                    {uc}
                  </li>
                ))}
              </ul>
            )}

            <div className="flex items-center justify-between mt-3 pt-2 border-t border-border/50">
              <button
                onClick={handleToggleLearned}
                className={`flex items-center gap-1 text-xs px-2 py-1 rounded-md transition-colors ${isLearned ? 'bg-primary/20 text-primary' : 'text-muted-foreground hover:text-foreground hover:bg-accent/20'}`}
                aria-label={isLearned ? (isFr ? 'Marquer comme non appris' : 'Mark as not learned') : (isFr ? 'Marquer comme appris' : 'Mark as learned')}
              >
                <Check className="w-3 h-3" />
                {isLearned ? (isFr ? 'Appris ✓' : 'Learned ✓') : (isFr ? 'Marquer appris' : 'Mark learned')}
              </button>
              <div className="flex items-center gap-2">
                <span className="text-[9px] text-muted-foreground/60">{learnedCount}/{cards.length}</span>
                <button onClick={() => { advance(); setPhase('active'); }} className="flex items-center gap-0.5 text-xs text-primary hover:text-primary/80 transition-colors" aria-label={isFr ? 'Question suivante' : 'Next question'}>
                  {isFr ? 'Suivant' : 'Next'}
                  <ChevronRight className="w-3 h-3" />
                </button>
              </div>
            </div>

            {allLearned && (
              <div className="mt-2 pt-2 border-t border-primary/20 text-center">
                <p className="text-xs text-primary font-semibold">🎉 {isFr ? 'Tout appris ! Bravo !' : 'All learned! Well done!'}</p>
                <button onClick={handleResetLearned} className="flex items-center gap-1 mx-auto mt-1 text-[10px] text-muted-foreground hover:text-foreground transition-colors" aria-label={isFr ? 'Recommencer' : 'Reset'}>
                  <RotateCcw className="w-3 h-3" />
                  {isFr ? 'Recommencer' : 'Reset'}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ── Active Ticker (default) ──
  return (
    <div className="fixed bottom-20 md:bottom-4 left-4 z-40 max-w-[calc(50vw-1rem)] md:max-w-sm" role="complementary" aria-label="CRR Flash Questions">
      <div
        className={`relative rounded-lg border border-primary/30 bg-background/95 backdrop-blur-sm shadow-lg shadow-primary/10 cursor-pointer ${settings.reducedMotion ? '' : 'transition-opacity duration-300'} ${transitioning ? 'opacity-0' : 'opacity-100'}`}
        onClick={() => setPhase('expanded')}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setPhase('expanded'); } }}
        tabIndex={0}
        role="button"
        aria-label={isFr ? 'Voir les détails de la question' : 'View question details'}
      >
        <button
          onClick={(e) => { e.stopPropagation(); setPhase('minimized'); }}
          className="absolute -top-2 -right-2 p-1 rounded-full bg-background border border-primary/30 hover:bg-accent/20 text-muted-foreground hover:text-foreground transition-colors"
          aria-label={isFr ? 'Réduire le ticker CRR' : 'Minimize CRR ticker'}
        >
          <X className="w-3 h-3" />
        </button>

        <div className="px-3 py-2.5" aria-live="polite">
          <div className="flex items-center gap-1.5 mb-1.5">
            <Radio className={`w-3.5 h-3.5 text-primary ${settings.reducedMotion ? '' : 'motion-safe:animate-pulse'}`} />
            <span className="text-[10px] font-orbitron font-bold text-primary tracking-widest uppercase">⚓ CRR Flash</span>
            <span className="text-[9px] text-muted-foreground/60 ml-auto">24H</span>
          </div>

          <p className="text-xs text-foreground leading-relaxed tracking-wide">
            {q}
            {isLearned && <Check className="inline w-3 h-3 text-primary ml-1" />}
          </p>

          {showAnswer && (
            <p className={`text-xs text-primary font-semibold mt-1 leading-relaxed tracking-wide ${settings.reducedMotion ? '' : 'animate-in fade-in duration-500'}`}>
              → {a}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};
